import BaseLocalizationService, { ILocalizationContainer } from "@edaz/services/baseLocalizationService";
import { bound } from "@frui.ts/helpers";
import { DatePickerLocalization } from "@edaz/edazkit/DatePicker";
import type { UploaderLocalization } from "@edaz/edazkit/Uploader";
import { action, computed, observable } from "mobx";
import LocalizationRepository from "repositories/localizationRepository";
import EventBus, { localeChanged } from "./eventBus";
import LocaleProvider from "./localeProvider";
import type { KnownErrorCodes } from "../config";
import {
  breakableHyphenWords,
  formatTextWithUnbreakableRules,
  unbreakableDate,
  unbreakableSpaces,
} from "../utils/localizationUtils";
import LanguageCode from "models/languageCode";
import CurrencyCode from "entities/currencyCode";

export interface LocalizationContainer extends ILocalizationContainer {
  language_code: string;
  attributes: ILocalizationContainer;
  general: ILocalizationContainer;
}

export default class LocalizationService extends BaseLocalizationService<LocalizationContainer> {
  @observable locs: LocalizationContainer;

  @computed
  get defaultPrefixNumber() {
    if (this.locale === "cs") {
      return "+420";
    } else {
      return undefined;
    }
  }

  constructor(
    private localeProvider: LocaleProvider,
    protected localizationProvider: LocalizationRepository,
    private eventBus: EventBus
  ) {
    super(localizationProvider);
    this.eventBus.subscribe(localeChanged, this.changeLocale);
  }

  get locale(): string {
    return this.localeProvider.locale;
  }

  // Get LanguageCode from current locale (i.e. en-GB => EN)
  get languageCode(): LanguageCode {
    return this.localeProvider.locale.substring(0, 2).toUpperCase() as LanguageCode;
  }

  translate(code: string, size?: number, disableUnbreakableRules?: boolean): string {
    return disableUnbreakableRules ? super.translate(code, size) : formatTextWithUnbreakableRules(super.translate(code, size));
  }

  @bound
  translateGeneral(code: string, size?: number, disableUnbreakableRules?: boolean) {
    return this.translate(`general.${code}`, size, disableUnbreakableRules);
  }

  @bound
  translateError(errorCode: KnownErrorCodes) {
    return this.translate(`general.errors.${errorCode}`);
  }

  @bound
  translateUploader(): UploaderLocalization {
    return {
      drag: this.translateGeneral("documentUploader.localization.drag"),
      or: this.translateGeneral("documentUploader.localization.or"),
      select: this.translateGeneral("documentUploader.localization.select"),
    };
  }

  get datePickerLocalization(): DatePickerLocalization {
    const days = ["su", "mo", "tu", "we", "th", "fr", "sa"];
    const getMonth = (month: string) => this.translateGeneral(`datepicker.strings.months.${month}`);
    const getDays = (length: string) => days.map(day => this.translateGeneral(`datepicker.strings.weekdays${length}.${day}`));
    const format = this.translateGeneral("datepicker.format");

    return {
      format: format === "general.datepicker.format" ? "" : format,
      strings: {
        months: [
          "january",
          "february",
          "march",
          "april",
          "may",
          "june",
          "july",
          "august",
          "september",
          "october",
          "november",
          "december",
        ].map(getMonth),
        weekdaysLong: getDays("Long"),
        weekdaysShort: getDays("Short"),
        firstDayOfWeek: days.indexOf(this.translateGeneral("datepicker.firstDayOfWeek")),
        fromDate: this.translateGeneral("datepicker.strings.fromDate"),
        toDate: this.translateGeneral("datepicker.strings.toDate"),
        todayButton: this.translateGeneral("datepicker.strings.todayButton"),
      },
    };
  }

  @bound
  translateAttribute(model: string, attribute: string, size?: number, disableUnbreakableRules?: boolean): string {
    return this.translate(`attributes.${model}.${attribute}`, size, disableUnbreakableRules);
  }

  @bound
  formatDateTime(date?: Date, options?: Intl.DateTimeFormatOptions): string {
    return unbreakableDate(super.formatDateTime(date, { ...options, timeZone: "Europe/Prague" }).replace(/(^|[-/])0+/g, "$1"));
  }

  @bound
  formatTime(time?: Date, options?: Intl.DateTimeFormatOptions): string {
    return super.formatTime(time, { ...options, timeZone: "Europe/Prague" });
  }

  @bound
  formatNumber(num: number, options?: Intl.NumberFormatOptions): string {
    return formatTextWithUnbreakableRules(super.formatNumber(num, options));
  }

  @bound
  formatPrice(price: number, currencyCode?: CurrencyCode): string {
    if (Number(price) !== price) {
      return "";
    }
    const priceLocaleCode = breakableHyphenWords(this.translate("priceLocaleCode"));
    if (currencyCode) {
      const formatter = new Intl.NumberFormat(priceLocaleCode === "priceLocaleCode" ? "cs-CS" : priceLocaleCode, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: currencyCode !== "EUR" ? "symbol" : "code",
        currencySign: "standard",
        minimumFractionDigits: 0,
      });

      const formatted = formatter.format(price);
      const parts = formatter.formatToParts(price);
      const numberPart = parts.find(part => part.type === "integer")?.value || "";
      const currencyPart = parts.find(part => part.type === "currency")?.value || "";

      if (formatted.startsWith(currencyPart)) {
        const numberOnly = formatted.replace(currencyPart, "").trim();
        return unbreakableSpaces(`${numberOnly} ${currencyPart}`);
      }

      return unbreakableSpaces(formatted);
    }

    const formatter = new Intl.NumberFormat(priceLocaleCode === "priceLocaleCode" ? "cs-CS" : priceLocaleCode, {
      style: "currency",
      currency: "CZK",
      minimumFractionDigits: 0,
    });

    return unbreakableSpaces(formatter.format(price));
  }

  @action.bound
  formatDate(date?: Date, options?: Intl.DateTimeFormatOptions): string {
    return super.formatDate(date, options).replace(/\.(?!\s)/g, ".\u00a0");
  }

  @action.bound
  changeLocale() {
    this.initialize();
  }
}
